import { Injectable, ProviderToken, inject } from '@angular/core';
import { AssignmentAPI } from '../../classes/assignment-api/assignment-api';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { MessageService } from 'src/app/modules/message/services/message/message.service';
import { MessageCodes } from 'src/app/shared/enums';
import { TimeElapsedService } from 'src/app/shared/services';
import { ResultsService } from 'src/app/shared/services/results/results.service';
import {
  ActivityStatus,
  ActivityValidStatus,
} from 'src/app/shared/enums/activity-status';
import { AuthSessionService } from 'src/app/modules/auth/services/auth-session/auth-session.service';
import { FeatureFlagService } from 'src/app/modules/feature-flags/services/feature-flag/feature-flag.service';
import { FeatureFlags } from 'src/app/shared/enums/feature-flags';
import { ContentMetadataService } from 'src/app/modules/content-metadata-api/services/content-metadata.service';
import { KnowledgeBaseService } from '../kb/kb.service';
import { BehaviorSubject } from 'rxjs';
import { AssignmentResponseObject } from 'src/app/modules/content-metadata-api/types/content-metadata';
import { UpdateAssignmentData } from '../../types';

const getAssignmentFacade = (): AssignmentAPI => {
  const injectionPoint: ProviderToken<AssignmentAPI> = AssignmentAPI;
  const facade = inject(injectionPoint);
  return facade;
};

@Injectable({
  providedIn: 'root',
})
export class AssignmentService {
  public assignment = getAssignmentFacade();
  assignmentIdFromApi = new BehaviorSubject<number>(-1);
  contentOid = new BehaviorSubject<number>(-1);
  matchedAssignment: AssignmentResponseObject | null = null;
  isAssignment = new BehaviorSubject<boolean>(false);

  constructor(
    private oidcSecurityService: OidcSecurityService,
    private messageService: MessageService,
    private timeElapsedService: TimeElapsedService,
    private resultService: ResultsService,
    private authSessionService: AuthSessionService,
    private featureFlagsService: FeatureFlagService,
    private contentMetadataService: ContentMetadataService,
    private kbService: KnowledgeBaseService
  ) {}

  async checkForAccessToken(): Promise<boolean> {
    if (this.assignment.authToken) {
      return true;
    }
    this.oidcSecurityService.getAccessToken().subscribe({
      next: token => {
        if (token) {
          this.assignment.setAuthToken(token);
        }
      },
      error: err => {
        console.error('We had issues getting an access token', err);
        this.messageService.showMessage(MessageCodes.HTTP_AUTH_REQUIRED);
      },
    });
    return true;
  }

  // eslint-disable-next-line complexity
  async updateAssignmentRecord(status: ActivityStatus): Promise<void> {
    if (
      !this.authSessionService.hasRole('student') ||
      this.authSessionService.isTeacher
    ) {
      console.warn(
        'Warning! Cannot update an assignment, because current user is not a student'
      );
      return;
    }
    if (
      !this.featureFlagsService.isFlagEnabled(FeatureFlags.USE_ASSIGNMENT_API)
    ) {
      console.warn(
        'Warning! Cannot use the Assignment API with the feature enabled'
      );
      return;
    }
    if (
      this.assignmentIdFromApi.getValue() === -1 &&
      this.contentOid.getValue() === -1
    ) {
      console.warn(
        'Warning! Cannot update the Assignment API without an assignment id or content id'
      );
      return;
    }
    if (
      await this.createOrFindActivityRecord(
        this.assignmentIdFromApi.getValue(),
        this.contentOid.getValue()
      )
    ) {
      const record = {
        id: this.assignmentIdFromApi.getValue(),
        numQuestionsCorrect: this.resultService.numCorrect,
        numQuestionsIncorrect: this.resultService.numIncorrect,
        score:
          status === ActivityStatus.IN_PROGRESS
            ? 0
            : this.resultService.getPercentage(),
        duration: this.timeElapsedService.getTimeAsSeconds(),
      };
      // If the status has changed, then we need to update it,
      // otherwise PATCH API will return 400 error
      if (
        (this.matchedAssignment &&
          this.matchedAssignment.status
            .toLocaleUpperCase()
            .replaceAll('_', '') !== status.toUpperCase()) ||
        !this.matchedAssignment
      ) {
        (record as UpdateAssignmentData).status = status;
        if (this.matchedAssignment) {
          this.matchedAssignment.status =
            this.parseActivityStatusToValidStatus(status);
        }
      }
      this.assignment.updateRecord([record]).catch(() => {
        this.messageService.showMessage(MessageCodes.HTTP_NET_GENERIC);
      });
    } else {
      console.error(
        'AssignmentService - update a record:: Error finding or creating a record for the following id and content id',
        this.assignmentIdFromApi.getValue(),
        this.contentOid.getValue()
      );
      this.messageService.showMessage(MessageCodes.HTTP_UNEXPECTED_NET_ERROR);
    }
  }

  async createOrFindActivityRecord(
    id: number,
    contentId: number
  ): Promise<boolean> {
    if (
      !this.authSessionService.hasRole('student') ||
      this.authSessionService.isTeacher
    ) {
      return false;
    }

    const existingRecord = await this.findActivityRecord(id, contentId);
    if (existingRecord) {
      this.isAssignment.next(existingRecord.isAssigned);
      this.matchedAssignment = { ...existingRecord };
      this.contentOid.next(existingRecord.contentId);
      this.assignmentIdFromApi.next(existingRecord.id);
      return true;
    }

    const recordId = await this.createNewAssignment(contentId);
    if (recordId !== -1) {
      const newRecord = await this.findActivityRecord(recordId, contentId);
      if (newRecord) {
        this.isAssignment.next(false);
        this.matchedAssignment = { ...newRecord };
        this.contentOid.next(newRecord.contentId);
        this.assignmentIdFromApi.next(newRecord.id);
        return true;
      } else {
        return false;
      }
    }

    return false;
  }

  async findActivityRecord(
    id: number,
    contentId: number
  ): Promise<AssignmentResponseObject | null> {
    const inProgressRecords = await this.getRecords(
      ActivityValidStatus.IN_PROGRESS.toString().toUpperCase()
    );
    const assignedRecords = await this.getRecords(
      ActivityValidStatus.ASSIGNED.toString().toUpperCase()
    );
    // make sure in progress is first in list so prioritize finding that status of record
    const records = [...inProgressRecords, ...assignedRecords];

    let record;

    record = this.findActivityRecordById(id, records);
    if (record) {
      return record;
    }
    record = this.findActivityRecordById(contentId, records, true);
    if (record) {
      return record;
    }

    return null;
  }

  findActivityRecordById(
    findByValue: number,
    records: AssignmentResponseObject[],
    findByContentId?: boolean
  ): AssignmentResponseObject | null {
    if (records) {
      let record;
      if (!findByContentId) {
        record = records.find(r => r.id === findByValue);
      } else {
        record = records.find(r => r.contentId === findByValue);
      }

      if (record) {
        return record;
      }
    }

    return null;
  }

  parseActivityStatusToValidStatus(status: ActivityStatus): string {
    switch (status) {
      case ActivityStatus.ASSIGNED:
        return ActivityValidStatus.ASSIGNED;
      case ActivityStatus.IN_PROGRESS:
        return ActivityValidStatus.IN_PROGRESS;
      case ActivityStatus.COMPLETED:
        return ActivityValidStatus.COMPLETED;
      default:
        return '';
    }
  }

  private async getRecords(
    status: string
  ): Promise<AssignmentResponseObject[]> {
    const records =
      await this.contentMetadataService.getContentAssignmentStudentData({
        studentIdFilter: [Number(this.kbService.kb.user_id.value)],
        statusFilter: status,
      });
    if (records) {
      return records;
    }

    return [];
  }

  private async createNewAssignment(id: number): Promise<number> {
    if (
      !this.authSessionService.hasRole('student') ||
      this.authSessionService.isTeacher
    ) {
      return -1;
    }

    const res = await this.assignment.createNewRecord([{ contentId: id }]);

    if (res) {
      this.assignmentIdFromApi.next(Number(res));
      this.contentOid.next(id);
      const record = {
        id: this.assignmentIdFromApi.getValue(),
        numQuestionsCorrect: this.resultService.numCorrect,
        numQuestionsIncorrect: this.resultService.numIncorrect,
        score: 0,
        duration: this.timeElapsedService.getTimeAsSeconds(),
      };
      await this.assignment.updateRecord([record]).catch(() => {
        this.messageService.showMessage(MessageCodes.HTTP_NET_GENERIC);
      });
      return Number(res);
    } else {
      this.messageService.showMessage(MessageCodes.HTTP_FORBIDDEN);
      return -1;
    }
  }
}
